import { Text, Group, Paper, Progress, Box } from "@mantine/core";
import { DataTable, Alert, Badge } from "@xplatform/design-system";
import type {
  DataTableColumn,
  DataTableFooterCell,
} from "@xplatform/design-system";
import {
  IconCurrencyDollar,
  IconCoins,
  IconFileText,
  IconActivity,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { ExternalRefCapabilityUsageViewModel } from "@models/monitoring";
import { formatCost, formatNumber, getCostPercent } from "@utils/formatters";

import "./CostBreakdownSection.scss";

interface CostBreakdownSectionProps {
  capabilityUsage: ExternalRefCapabilityUsageViewModel[];
}

export const CostBreakdownSection = ({
  capabilityUsage,
}: CostBreakdownSectionProps) => {
  const { t } = useTranslation();

  if (capabilityUsage.length === 0) {
    return (
      <Alert
        icon={<IconCurrencyDollar size={16} />}
        color="gray"
        variant="light"
      >
        {t("admin.monitoring.costBreakdown.noData")}
      </Alert>
    );
  }

  const totalCost = capabilityUsage.reduce(
    (sum, e) => sum + parseFloat(e.totalCost),
    0,
  );
  const totalEvents = capabilityUsage.reduce(
    (sum, e) => sum + e.eventCount,
    0,
  );
  const totalInputTokens = capabilityUsage.reduce(
    (sum, e) => sum + (e.inputTokens ?? 0),
    0,
  );
  const totalOutputTokens = capabilityUsage.reduce(
    (sum, e) => sum + (e.outputTokens ?? 0),
    0,
  );
  const totalTokens = capabilityUsage.reduce(
    (sum, e) => sum + (e.totalTokens ?? 0),
    0,
  );
  const totalPages = capabilityUsage.reduce(
    (sum, e) => sum + (e.pagesProcessed ?? 0),
    0,
  );
  const currency = capabilityUsage[0].currency;

  const columns: DataTableColumn<ExternalRefCapabilityUsageViewModel>[] = [
    {
      key: "eventType",
      header: t("admin.monitoring.costBreakdown.eventType"),
      width: "20%",
      render: (item) => (
        <Badge variant="light" size="sm" radius="sm">
          {item.capability}
        </Badge>
      ),
    },
    {
      key: "events",
      header: t("admin.monitoring.costBreakdown.events"),
      width: "10%",
      align: "right",
      render: (item) => <Text size="sm">{item.eventCount.toLocaleString()}</Text>,
    },
    {
      key: "inputTokens",
      header: t("admin.monitoring.costBreakdown.inputTokens"),
      width: "13%",
      align: "right",
      render: (item) => <Text size="sm">{formatNumber(item.inputTokens)}</Text>,
    },
    {
      key: "outputTokens",
      header: t("admin.monitoring.costBreakdown.outputTokens"),
      width: "13%",
      align: "right",
      render: (item) => <Text size="sm">{formatNumber(item.outputTokens)}</Text>,
    },
    {
      key: "totalTokens",
      header: t("admin.monitoring.costBreakdown.totalTokens"),
      width: "13%",
      align: "right",
      render: (item) => (
        <Text size="sm" fw={500}>
          {formatNumber(item.totalTokens)}
        </Text>
      ),
    },
    {
      key: "pages",
      header: t("admin.monitoring.costBreakdown.pages"),
      width: "8%",
      align: "right",
      render: (item) => <Text size="sm">{formatNumber(item.pagesProcessed)}</Text>,
    },
    {
      key: "cost",
      header: t("admin.monitoring.costBreakdown.cost"),
      width: "23%",
      align: "right",
      render: (item) => {
        const pct = getCostPercent(item.totalCost, totalCost);
        return (
          <Box>
            <Group gap={6} justify="flex-end" mb={4}>
              <Text size="sm" fw={500}>
                {formatCost(item.totalCost, item.currency)}
              </Text>
              <Badge
                variant="light"
                color={pct > 50 ? "blue" : "gray"}
                size="xs"
                radius="sm"
              >
                {pct.toFixed(1)}%
              </Badge>
            </Group>
            <Progress
              value={pct}
              size={4}
              color={pct > 50 ? "blue" : "gray"}
              radius="xl"
            />
          </Box>
        );
      },
    },
  ];

  const footer: DataTableFooterCell[] = [
    {
      content: (
        <Text size="sm" fw={700}>
          {t("admin.monitoring.costBreakdown.total")}
        </Text>
      ),
    },
    {
      content: (
        <Text size="sm" fw={600}>
          {totalEvents.toLocaleString()}
        </Text>
      ),
      align: "right",
    },
    {
      content: (
        <Text size="sm" fw={600}>
          {formatNumber(totalInputTokens || null)}
        </Text>
      ),
      align: "right",
    },
    {
      content: (
        <Text size="sm" fw={600}>
          {formatNumber(totalOutputTokens || null)}
        </Text>
      ),
      align: "right",
    },
    {
      content: (
        <Text size="sm" fw={600}>
          {formatNumber(totalTokens || null)}
        </Text>
      ),
      align: "right",
    },
    {
      content: (
        <Text size="sm" fw={600}>
          {formatNumber(totalPages || null)}
        </Text>
      ),
      align: "right",
    },
    {
      content: (
        <Text size="sm" fw={700} c="blue">
          {formatCost(totalCost.toString(), currency)}
        </Text>
      ),
      align: "right",
    },
  ];

  return (
    <div className="cost-breakdown">
      <Group className="cost-breakdown__stats" grow>
        <Paper className="cost-breakdown__stat cost-breakdown__stat--blue">
          <div className="cost-breakdown__stat-icon">
            <IconCurrencyDollar size={20} />
          </div>
          <div className="cost-breakdown__stat-content">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1}>
              {t("admin.monitoring.costBreakdown.cost")}
            </Text>
            <Text size="lg" fw={700} lh={1.2}>
              {formatCost(totalCost.toString(), currency)}
            </Text>
          </div>
        </Paper>
        <Paper className="cost-breakdown__stat cost-breakdown__stat--teal">
          <div className="cost-breakdown__stat-icon">
            <IconActivity size={20} />
          </div>
          <div className="cost-breakdown__stat-content">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1}>
              {t("admin.monitoring.costBreakdown.events")}
            </Text>
            <Text size="lg" fw={700} lh={1.2}>
              {totalEvents.toLocaleString()}
            </Text>
          </div>
        </Paper>
        <Paper className="cost-breakdown__stat cost-breakdown__stat--violet">
          <div className="cost-breakdown__stat-icon">
            <IconCoins size={20} />
          </div>
          <div className="cost-breakdown__stat-content">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1}>
              {t("admin.monitoring.costBreakdown.totalTokens")}
            </Text>
            <Text size="lg" fw={700} lh={1.2}>
              {formatNumber(totalTokens || null)}
            </Text>
          </div>
        </Paper>
        {totalPages > 0 && (
          <Paper className="cost-breakdown__stat cost-breakdown__stat--orange">
            <div className="cost-breakdown__stat-icon">
              <IconFileText size={20} />
            </div>
            <div className="cost-breakdown__stat-content">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1}>
                {t("admin.monitoring.costBreakdown.pages")}
              </Text>
              <Text size="lg" fw={700} lh={1.2}>
                {totalPages.toLocaleString()}
              </Text>
            </div>
          </Paper>
        )}
      </Group>

      <Paper withBorder radius="sm" className="cost-breakdown__table-wrapper">
        <DataTable<ExternalRefCapabilityUsageViewModel>
          highlightOnHover
          verticalSpacing="sm"
          horizontalSpacing="md"
          columns={columns}
          data={capabilityUsage}
          getRowKey={(item) => item.capability}
          footer={footer}
        />
      </Paper>
    </div>
  );
};
